import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Loader2, ArrowLeft, CheckCircle2, AlertCircle, Pencil, Check, Sparkles, X, Copy, Download, MessageSquare, ExternalLink, ShieldAlert } from "lucide-react";
import { createPortfolio, createProject, approvePlan, expandCampaign, pollJob } from "@/lib/agentApi";

// The Workroom / compliance / brief pages are complete HTML pages served by the
// agent backend itself — the deliverable IS the product. We link straight to them.
const AGENT_PUBLIC_BASE = "https://api.craigmaloney.com";
const captainUrl = (clientId) => `${AGENT_PUBLIC_BASE}/clients/${clientId}/workroom?panel=1`;
const complianceUrl = (clientId) => `${AGENT_PUBLIC_BASE}/clients/${clientId}/compliance`;
const PORTFOLIO_KEY = "cm_portfolioId";

const usd = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const STAGE_COPY = {
  numbers_ready: "Running the numbers — building your CAC glide path and pricing…",
  strategy_ready: "Writing your agent-team strategy…",
  content_ready: "Producing your launch content…",
};

const EMPTY_INTAKE = {
  businessName: "",
  businessModel: "B2B",
  llmsSource: "",
  icp: "",
  mediaCeiling: "",
  targetCac: "",
  primaryCta: "",
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-800">{label}</span>
    {hint && <span className="ml-2 text-xs text-slate-400">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const IntakeForm = ({ intake, setIntake, onSubmit, error }) => {
  const set = (k) => (e) => setIntake({ ...intake, [k]: e.target.value });
  const disabled = !intake.businessName.trim();
  return (
    <div data-testid="intake-form" className="space-y-5">
      <Field label="Business name">
        <input
          data-testid="input-businessName"
          className={inputCls}
          value={intake.businessName}
          onChange={set("businessName")}
          placeholder="Acme Coffee Roasters"
        />
      </Field>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Business model">
          <select
            data-testid="input-businessModel"
            className={inputCls}
            value={intake.businessModel}
            onChange={set("businessModel")}
          >
            <option value="B2B">B2B</option>
            <option value="B2C">B2C</option>
          </select>
        </Field>
        <Field label="Primary call-to-action" hint="optional">
          <input
            data-testid="input-primaryCta"
            className={inputCls}
            value={intake.primaryCta}
            onChange={set("primaryCta")}
            placeholder="Book a demo"
          />
        </Field>
      </div>

      <Field label="llms.txt URL" hint="feeds the system your business context">
        <input
          data-testid="input-llmsSource"
          className={inputCls}
          value={intake.llmsSource}
          onChange={set("llmsSource")}
          placeholder="https://yoursite.com/llms.txt"
        />
      </Field>

      <Field label="Ideal customer profile" hint="optional">
        <textarea
          data-testid="input-icp"
          rows={3}
          className={inputCls}
          value={intake.icp}
          onChange={set("icp")}
          placeholder="Who are you trying to reach?"
        />
      </Field>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Monthly media budget" hint="optional, USD">
          <input
            data-testid="input-mediaCeiling"
            type="number"
            className={inputCls}
            value={intake.mediaCeiling}
            onChange={set("mediaCeiling")}
            placeholder="5000"
          />
        </Field>
        <Field label="Target CAC" hint="optional, USD">
          <input
            data-testid="input-targetCac"
            type="number"
            className={inputCls}
            value={intake.targetCac}
            onChange={set("targetCac")}
            placeholder="40"
          />
        </Field>
      </div>

      {error && (
        <div
          data-testid="form-error"
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        data-testid="run-strategy-btn"
        onClick={onSubmit}
        disabled={disabled}
        className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
      >
        Run my strategy
      </button>
    </div>
  );
};

const Working = ({ label }) => (
  <div
    data-testid="working-state"
    className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-slate-50/60 px-6 py-20 text-center"
  >
    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    <p className="mt-5 max-w-md text-base text-slate-600">{label}</p>
    <p className="mt-2 text-xs text-slate-400">
      This can take a minute or two — the agents are working.
    </p>
  </div>
);

const GlideChart = ({ glide }) => {
  if (!glide?.data?.length) return null;
  return (
    <div data-testid="glide-chart" className="rounded-xl border border-slate-200 p-6">
      <h3 className="text-lg font-medium text-slate-900">CAC glide path</h3>
      <p className="mt-1 text-sm text-slate-500">
        Projected cost per acquisition dropping {glide.improvement}% — from{" "}
        {usd(glide.startCac)} to {usd(glide.endCac)} over 12 months.
      </p>
      <div className="mt-6 h-64 w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <LineChart data={glide.data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
            <Tooltip formatter={(v) => usd(v)} labelStyle={{ color: "#0f172a" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
            <Line type="monotone" dataKey="cac" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3, fill: "#2563eb" }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const TeamCard = ({ team, i }) => (
  <div
    data-testid={`team-card-${i}`}
    className="rounded-xl border border-slate-200 bg-slate-50/60 p-6"
  >
    <h4 className="text-base font-medium text-slate-900">{team.name}</h4>
    {team.focus && (
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{team.focus}</p>
    )}
    {team.tactics?.length > 0 && (
      <ul className="mt-4 space-y-2">
        {team.tactics.map((t, ti) => (
          <li key={ti} className="flex items-start gap-2 text-sm text-slate-700">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const PricingBlock = ({ pricing, cash }) => (
  <div className="grid grid-cols-1 gap-6 min-[641px]:grid-cols-2">
    <div data-testid="pricing-block" className="rounded-xl border border-slate-200 p-6">
      <h3 className="text-lg font-medium text-slate-900">Your pricing</h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-4xl font-medium text-slate-900">{usd(pricing?.monthlyPrice)}</span>
        <span className="text-sm text-slate-500">/ month</span>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        {usd(pricing?.startupOneTime)} one-time setup
      </p>
      {pricing?.note && (
        <p className="mt-4 text-sm leading-relaxed text-slate-600">{pricing.note}</p>
      )}
    </div>
    <div data-testid="cash-block" className="rounded-xl border border-slate-200 p-6">
      <h3 className="text-lg font-medium text-slate-900">Cash to fund the plan</h3>
      {cash?.note && <p className="mt-1 text-sm text-slate-500">{cash.note}</p>}
      <div className="mt-4 space-y-3">
        {cash?.horizons?.map((h) => (
          <div key={h.label} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm">
            <span className="text-slate-600">{h.label}</span>
            <span className="font-medium text-slate-900">{usd(h.totalCashOut)}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const StrategyView = ({ result, clientId, onApprove, error, complianceError }) => (
  <div data-testid="strategy-view" className="space-y-10">
    <div>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
        <CheckCircle2 className="h-3.5 w-3.5" /> Strategy ready
      </span>
      <h2 className="mt-4 text-2xl font-medium tracking-tight text-slate-900 sm:text-3xl">
        Growth plan for {result?.summary?.businessName}
      </h2>
    </div>

    <GlideChart glide={result?.glide} />

    <div>
      <h3 className="text-lg font-medium text-slate-900">Agent teams &amp; tactics</h3>
      <div className="mt-5 grid grid-cols-1 gap-5 min-[641px]:grid-cols-2">
        {result?.teams?.map((team, i) => (
          <TeamCard key={team.key || i} team={team} i={i} />
        ))}
      </div>
    </div>

    <PricingBlock pricing={result?.pricing} cash={result?.cash} />

    {complianceError && clientId && (
      <div data-testid="approve-compliance-error" className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-medium">A compliance check needs your attention before we generate the work.</p>
          <a
            data-testid="approve-compliance-link"
            href={complianceUrl(clientId)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1.5 font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950"
          >
            Review &amp; resolve compliance <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    )}

    {error && !complianceError && (
      <div data-testid="approve-error" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    )}

    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-6">
      <p className="text-base font-medium text-slate-900">Happy with the direction?</p>
      <p className="mt-1 text-sm text-slate-600">
        Use your Captain to read the diagnosis, weigh the options, and adjust the
        strategy. When you&apos;re happy, generate the work.
      </p>
      <button
        data-testid="approve-plan-btn"
        onClick={onApprove}
        className="mt-4 inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
      >
        Generate the work — I&apos;ve approved the strategy.
      </button>
    </div>
  </div>
);

const TYPE_LABEL = { email: "Email", social: "Social post", landing: "Landing page", ad: "Ad" };

const SIMPLE_FIELDS = [
  { key: "headline", label: "Headline" },
  { key: "body", label: "Body", multiline: true },
  { key: "label", label: "Label" },
];

const buildCopyText = (item) => {
  const d = item.data || {};
  const lines = [];
  if (item.type === "email" && d.subject !== undefined) {
    if (d.subject) lines.push(`Subject: ${d.subject}`);
    if (d.preview) lines.push(`Preview: ${d.preview}`);
    if (d.greeting) lines.push(d.greeting);
    (d.body || []).forEach((l) => lines.push(l));
    if (d.ctaLabel) lines.push(`[${d.ctaLabel}]`);
    if (d.signoff) lines.push(d.signoff);
  } else if (item.type === "social" && d.caption !== undefined) {
    if (d.caption) lines.push(d.caption);
    if (d.tag) lines.push(d.tag);
  } else if (item.type === "landing" && d.bullets !== undefined) {
    if (d.headline) lines.push(d.headline);
    if (d.sub) lines.push(d.sub);
    (d.bullets || []).forEach((b) => lines.push(`• ${b}`));
    if (d.url) lines.push(d.url);
  } else {
    if (d.headline) lines.push(d.headline);
    if (d.body) lines.push(d.body);
    if (d.label) lines.push(`[${d.label}]`);
  }
  return lines.join("\n");
};

const decodeSvg = (dataUri) => {
  try {
    const b64 = (dataUri || "").split(",")[1];
    return b64 ? atob(b64) : "";
  } catch (e) {
    return "";
  }
};

const editableFields = (type) => {
  if (type === "email")
    return [
      { key: "subject", label: "Subject line" },
      { key: "preview", label: "Preview text" },
      { key: "body", label: "Body (one paragraph per line)", multiline: true, isArray: true },
      { key: "ctaLabel", label: "Button label" },
    ];
  if (type === "social")
    return [
      { key: "caption", label: "Caption", multiline: true },
      { key: "tag", label: "Hashtag / tag" },
    ];
  if (type === "landing")
    return [
      { key: "headline", label: "Headline" },
      { key: "sub", label: "Subheadline", multiline: true },
      { key: "bullets", label: "Bullets (one per line)", multiline: true, isArray: true },
    ];
  return SIMPLE_FIELDS;
};

const AssetCard = ({ item, index, brand, approved, onSave, onApprove, simple = false }) => {
  const primary = brand?.primary || "#2563eb";
  const accent = brand?.accent || "#3457D5";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.data || {});
  const [copied, setCopied] = useState("");
  const fields = simple ? SIMPLE_FIELDS : editableFields(item.type);
  const d = item.data || {};

  const flash = (which) => {
    setCopied(which);
    setTimeout(() => setCopied(""), 1500);
  };
  const copyText = async () => {
    try { await navigator.clipboard.writeText(buildCopyText(item)); flash("text"); } catch (e) { /* noop */ }
  };
  const copySvg = async () => {
    try { await navigator.clipboard.writeText(decodeSvg(item.image)); flash("svg"); } catch (e) { /* noop */ }
  };
  const generic = simple || item.type === "ad";

  const setField = (f, value) =>
    setDraft((prev) => ({
      ...prev,
      [f.key]: f.isArray ? value.split("\n").filter((l) => l.trim() !== "") : value,
    }));
  const fieldValue = (f) => {
    const v = draft[f.key];
    if (f.isArray) return Array.isArray(v) ? v.join("\n") : v || "";
    return v || "";
  };
  const startEdit = () => {
    setDraft(item.data || {});
    setEditing(true);
  };
  const save = () => {
    onSave(draft);
    setEditing(false);
  };

  return (
    <div
      data-testid={`asset-card-${index}`}
      className="flex flex-col overflow-hidden rounded-2xl border bg-white transition-shadow"
      style={{ borderColor: approved ? accent : "#e2e8f0", boxShadow: approved ? `0 0 0 1.5px ${accent}` : "none" }}
    >
      <div className="flex h-48 items-center justify-center border-b border-slate-100 bg-slate-50 p-3">
        <img
          src={item.image}
          alt={item.title}
          data-testid={`asset-image-${index}`}
          className="max-h-full max-w-full rounded-md object-contain"
        />
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-slate-900">{item.title}</span>
          <span
            className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide"
            style={{ background: `${accent}1a`, color: primary }}
          >
            {TYPE_LABEL[item.type] || item.type}
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-400">{item.team} team</p>

        {!editing ? (
          <div className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">
            {generic && (
              <>
                {d.headline && <p className="font-medium text-slate-900">{d.headline}</p>}
                {d.body && <p className="mt-2 whitespace-pre-line">{d.body}</p>}
                {d.label && (
                  <span className="mt-3 inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium text-white" style={{ background: primary }}>
                    {d.label}
                  </span>
                )}
              </>
            )}
            {!generic && item.type === "email" && (
              <>
                <p className="font-medium text-slate-900">{d.subject}</p>
                <p className="text-xs text-slate-400">{d.preview}</p>
                {d.body?.map((l, li) => (
                  <p key={li} className="mt-2">{l}</p>
                ))}
                {d.ctaLabel && (
                  <span className="mt-3 inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ background: primary }}>
                    {d.ctaLabel}
                  </span>
                )}
              </>
            )}
            {!generic && item.type === "social" && (
              <>
                <p className="whitespace-pre-line">{d.caption}</p>
                {d.tag && <p className="mt-2 font-medium" style={{ color: accent }}>{d.tag}</p>}
              </>
            )}
            {!generic && item.type === "landing" && (
              <>
                <p className="font-medium text-slate-900">{d.headline}</p>
                <p className="mt-1 text-slate-600">{d.sub}</p>
                {d.bullets?.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {d.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: accent }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="mt-4 flex-1 space-y-3">
            {fields.map((f) => (
              <label key={f.key} className="block">
                <span className="text-xs font-medium text-slate-500">{f.label}</span>
                {f.multiline ? (
                  <textarea
                    data-testid={`edit-field-${index}-${f.key}`}
                    rows={f.isArray ? 4 : 2}
                    value={fieldValue(f)}
                    onChange={(e) => setField(f, e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                ) : (
                  <input
                    data-testid={`edit-field-${index}-${f.key}`}
                    value={fieldValue(f)}
                    onChange={(e) => setField(f, e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                )}
              </label>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center gap-2">
          {!editing ? (
            <>
              <button
                data-testid={`asset-edit-${index}`}
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                data-testid={`asset-approve-${index}`}
                onClick={onApprove}
                className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
                style={{ background: approved ? accent : primary }}
              >
                <Check className="h-3.5 w-3.5" /> {approved ? "Approved" : "Approve"}
              </button>
            </>
          ) : (
            <>
              <button
                data-testid={`asset-save-${index}`}
                onClick={save}
                className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
                style={{ background: primary }}
              >
                <Check className="h-3.5 w-3.5" /> Save
              </button>
              <button
                data-testid={`asset-cancel-${index}`}
                onClick={() => setEditing(false)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
            </>
          )}
        </div>

        {!editing && (
          <div className="mt-3 flex items-center gap-4 border-t border-slate-100 pt-3">
            <button
              data-testid={`asset-copy-text-${index}`}
              onClick={copyText}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-800"
            >
              <Copy className="h-3.5 w-3.5" /> {copied === "text" ? "Copied!" : "Copy text"}
            </button>
            <button
              data-testid={`asset-copy-svg-${index}`}
              onClick={copySvg}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-800"
            >
              <Copy className="h-3.5 w-3.5" /> {copied === "svg" ? "Copied!" : "Copy SVG"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CampaignView = ({ campaign, brand }) => {
  const primary = brand?.primary || "#2563eb";
  const accent = brand?.accent || primary;
  const [assets, setAssets] = useState(campaign?.assets || []);
  const [approved, setApproved] = useState({});
  const save = (i, data) =>
    setAssets((prev) => prev.map((a, idx) => (idx === i ? { ...a, data } : a)));
  const approve = (id) => setApproved((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div data-testid="campaign-view" className="space-y-8">
      <div className="rounded-2xl p-7 text-white" style={{ background: primary }}>
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: accent }}>
          Full campaign
        </span>
        <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">{campaign?.theme}</h2>
        {campaign?.summary && <p className="mt-2 max-w-2xl text-sm text-white/80">{campaign.summary}</p>}
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {campaign?.durationWeeks && (
            <span className="rounded-full bg-white/15 px-3 py-1">{campaign.durationWeeks} weeks</span>
          )}
          {campaign?.channels?.map((c) => (
            <span key={c} className="rounded-full bg-white/15 px-3 py-1 capitalize">{c}</span>
          ))}
        </div>
      </div>

      {campaign?.calendar?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-slate-900">Content calendar</h3>
          <div className="mt-4 space-y-4">
            {campaign.calendar.map((wk) => (
              <div key={wk.week} data-testid={`calendar-week-${wk.week}`} className="rounded-xl border border-slate-200 p-5">
                <p className="text-sm font-semibold" style={{ color: primary }}>Week {wk.week}</p>
                <div className="mt-3 divide-y divide-slate-100">
                  {wk.items?.map((it, ii) => (
                    <div key={ii} className="flex items-center gap-3 py-2 text-sm">
                      <span className="w-10 shrink-0 text-xs font-medium text-slate-400">{it.day}</span>
                      <span className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize" style={{ background: `${accent}1a`, color: primary }}>
                        {it.channel}
                      </span>
                      <span className="text-slate-700">{it.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {assets.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-slate-900">Campaign assets</h3>
          <div className="mt-4 grid grid-cols-1 gap-6 min-[641px]:grid-cols-2">
            {assets.map((item, i) => (
              <AssetCard
                key={item.id || i}
                item={item}
                index={i}
                brand={brand}
                approved={!!approved[item.id || i]}
                onSave={(data) => save(i, data)}
                onApprove={() => approve(item.id || i)}
                simple
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ContentView = ({ result, clientId, onRestart }) => {
  const brand = result?.brand || {};
  const primary = brand.primary || "#2563eb";
  const accent = brand.accent || primary;
  const [assets, setAssets] = useState(result?.gallery || []);
  const [approved, setApproved] = useState({});
  const [expandStatus, setExpandStatus] = useState("idle"); // idle|loading|done|error
  const [campaign, setCampaign] = useState(null);
  const [campaignBrand, setCampaignBrand] = useState(brand);
  const [expandError, setExpandError] = useState("");
  const [expandCompliance, setExpandCompliance] = useState(false);

  const handleSave = (i, data) =>
    setAssets((prev) => prev.map((a, idx) => (idx === i ? { ...a, data } : a)));
  const handleApprove = (id) =>
    setApproved((prev) => ({ ...prev, [id]: !prev[id] }));
  const approvedCount = Object.values(approved).filter(Boolean).length;
  const approvedAssets = assets.filter((a, i) => approved[a.id || i]);

  const runExpand = async () => {
    setExpandError("");
    setExpandCompliance(false);
    setExpandStatus("loading");
    try {
      const { jobId } = await expandCampaign(clientId, { approved: approvedAssets, theme: "" });
      const job = await pollJob(jobId, "campaign_ready");
      setCampaign(job.result?.campaign);
      setCampaignBrand(job.result?.brand || brand);
      setExpandStatus("done");
    } catch (e) {
      setExpandCompliance(!!e.compliance);
      setExpandError(e.message);
      setExpandStatus("error");
    }
  };

  const downloadApprovedText = () => {
    const list = approvedAssets.length ? approvedAssets : assets;
    const text = list
      .map((a) => `### ${a.title} (${a.type})\n${buildCopyText(a)}`)
      .join("\n\n----------------------------------------\n\n");
    const blob = new Blob([text || "No assets."], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const slug = (result?.summary?.businessName || "launch")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${slug}-assets.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="content-view" className="space-y-8">
      <div>
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-white" style={{ background: primary }}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Content ready
        </span>
        <h2 className="mt-4 text-2xl font-medium tracking-tight text-slate-900 sm:text-3xl">
          Your launch assets
        </h2>
        <p className="mt-2 text-base text-slate-600">
          On-brand samples produced by your agent teams for{" "}
          {result?.summary?.businessName}. Tweak the copy, approve what you like,
          then expand into a full campaign.
        </p>
        {assets.length > 0 && (
          <p data-testid="approved-count" className="mt-2 text-sm text-slate-400">
            {approvedCount} of {assets.length} approved
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 min-[641px]:grid-cols-2">
        {assets.map((item, i) => (
          <AssetCard
            key={item.id || i}
            item={item}
            index={i}
            brand={brand}
            approved={!!approved[item.id || i]}
            onSave={(data) => handleSave(i, data)}
            onApprove={() => handleApprove(item.id || i)}
          />
        ))}
      </div>

      {assets.length > 0 && (
        <button
          data-testid="download-approved-btn"
          onClick={downloadApprovedText}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          <Download className="h-4 w-4" /> Download {approvedCount > 0 ? "approved" : "all"} copy (.txt)
        </button>
      )}

      {expandStatus === "done" && campaign ? (
        <CampaignView campaign={campaign} brand={campaignBrand} />
      ) : (
        <div className="rounded-2xl border border-slate-200 p-6" style={{ background: `${accent}0d` }}>
          {expandStatus === "loading" ? (
            <div data-testid="expand-loading" className="flex flex-col items-center justify-center py-8 text-center">
              <Loader2 className="h-7 w-7 animate-spin" style={{ color: primary }} />
              <p className="mt-4 text-base font-medium text-slate-900">Building your full campaign…</p>
              <p className="mt-1 max-w-md text-sm text-slate-600">
                Content calendar + multi-channel asset set. This takes about a minute.
              </p>
            </div>
          ) : (
            <div className="sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-base font-medium text-slate-900">Ready to go bigger?</p>
                <p className="mt-1 text-sm text-slate-600">
                  Turn these samples into a full campaign — a content calendar and a
                  multi-channel asset set.
                </p>
                {expandStatus === "error" && expandCompliance && (
                  <div data-testid="expand-compliance-error" className="mt-2 flex items-start gap-2 text-sm text-amber-800">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      A compliance check needs your attention.{" "}
                      <a
                        data-testid="expand-compliance-link"
                        href={complianceUrl(clientId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline underline-offset-2"
                      >
                        Resolve it here
                      </a>
                      .
                    </span>
                  </div>
                )}
                {expandStatus === "error" && !expandCompliance && (
                  <p data-testid="expand-error" className="mt-2 text-sm text-red-600">{expandError}</p>
                )}
              </div>
              <button
                data-testid="expand-campaign-btn"
                onClick={runExpand}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 sm:mt-0"
                style={{ background: primary }}
              >
                <Sparkles className="h-4 w-4" /> {expandStatus === "error" ? "Try again" : "Expand into campaign"}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-6">
        <p className="text-base font-medium text-slate-900">What&apos;s next?</p>
        <p className="mt-1 text-sm text-slate-600">
          Keep refining this campaign with your Captain on the side, or spin up a
          strategy for a different business.
        </p>
        <button
          data-testid="restart-btn"
          onClick={onRestart}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-800 transition-colors hover:border-slate-400 hover:bg-slate-50"
        >
          <Sparkles className="h-4 w-4" /> Start a new business
        </button>
      </div>
    </div>
  );
};

const CaptainPanel = ({ clientId }) => (
  <aside data-testid="captain-panel" className="lg:sticky lg:top-6 lg:self-start">
    <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2.5 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
          <MessageSquare className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">Your Captain</p>
          <p className="truncate text-xs text-slate-500">Ask questions, weigh options, adjust the plan</p>
        </div>
      </div>
      <iframe
        data-testid="captain-iframe"
        title="Your Captain"
        src={captainUrl(clientId)}
        className="h-[560px] w-full border-0 lg:h-[calc(100vh-9rem)]"
        allow="clipboard-write"
      />
    </div>
  </aside>
);

export default function StrategicRoi() {
  const [step, setStep] = useState("form"); // form | working | strategy | content
  const [intake, setIntake] = useState(EMPTY_INTAKE);
  const [clientId, setClientId] = useState(null);
  const [result, setResult] = useState(null);
  const [stageLabel, setStageLabel] = useState("");
  const [error, setError] = useState("");
  const [complianceError, setComplianceError] = useState(false);
  const [strategyReached, setStrategyReached] = useState(false);

  // Ensure a portfolio exists (persisted per browser), then add this business to
  // it. Every business — including the first — is routed through new-project so
  // "New business" is just another call and the 5-business cap comes for free.
  const startProject = async (intakeBody) => {
    let pid = localStorage.getItem(PORTFOLIO_KEY);
    if (!pid) {
      const { portfolioId } = await createPortfolio();
      pid = portfolioId;
      localStorage.setItem(PORTFOLIO_KEY, pid);
    }
    try {
      return await createProject(pid, intakeBody);
    } catch (e) {
      if (e.portfolioFull) throw e;
      // Stale/unknown portfolio id — recreate once and retry.
      if (e.notFound) {
        console.warn("Stored portfolioId was not found upstream; recreating.");
        const { portfolioId } = await createPortfolio();
        localStorage.setItem(PORTFOLIO_KEY, portfolioId);
        return await createProject(portfolioId, intakeBody);
      }
      throw e;
    }
  };

  const handleStart = async () => {
    setError("");
    setStep("working");
    setStageLabel("Creating your workspace…");
    try {
      const { clientId: cid, jobId } = await startProject(intake);
      setClientId(cid);
      setStageLabel(STAGE_COPY.numbers_ready);
      const job = await pollJob(jobId, "strategy_ready", (j) => {
        if (j.result) setResult(j.result);
        if (STAGE_COPY[j.stage]) setStageLabel(STAGE_COPY[j.stage]);
      });
      setResult(job.result);
      setStrategyReached(true);
      setStep("strategy");
    } catch (e) {
      setError(
        e.portfolioFull
          ? "You've reached the 5-business limit for this portfolio. Wrap up or remove one to add another."
          : e.message,
      );
      setStep("form");
    }
  };

  const handleApprove = async () => {
    setError("");
    setComplianceError(false);
    setStep("working");
    setStageLabel(STAGE_COPY.content_ready);
    try {
      const { jobId } = await approvePlan(clientId);
      const job = await pollJob(jobId, "content_ready", (j) => {
        if (j.result) setResult(j.result);
      });
      setResult(job.result);
      setStep("content");
    } catch (e) {
      setComplianceError(!!e.compliance);
      setError(e.message);
      setStep("strategy");
    }
  };

  const handleRestart = () => {
    setIntake(EMPTY_INTAKE);
    setClientId(null);
    setResult(null);
    setError("");
    setComplianceError(false);
    setStrategyReached(false);
    setStep("form");
  };

  const showCaptain = strategyReached && clientId;
  const wrapCls = showCaptain ? "max-w-6xl" : "max-w-4xl";

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white text-slate-900">
      <header className="border-b border-slate-200">
        <div className={`mx-auto flex ${wrapCls} items-center justify-between px-6 py-4`}>
          <Link
            data-testid="back-home-link"
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
          >
            <ArrowLeft className="h-4 w-4" /> Craig Maloney
          </Link>
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Strategic ROI
          </span>
        </div>
      </header>

      <main className={`mx-auto ${wrapCls} px-6 py-14`}>
        {!showCaptain ? (
          <>
            {step === "form" && (
              <>
                <h1 className="text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
                  Get your custom growth strategy
                </h1>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
                  Tell the system about your business and it will run a live audit —
                  CAC glide path, agent-team strategy, pricing, and ready-to-ship
                  launch content.
                </p>
                <div className="mt-10 max-w-2xl">
                  <IntakeForm
                    intake={intake}
                    setIntake={setIntake}
                    onSubmit={handleStart}
                    error={error}
                  />
                </div>
              </>
            )}

            {step === "working" && <Working label={stageLabel} />}
          </>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
            <div className="min-w-0">
              {step === "working" && <Working label={stageLabel} />}

              {step === "strategy" && (
                <StrategyView
                  result={result}
                  clientId={clientId}
                  onApprove={handleApprove}
                  error={error}
                  complianceError={complianceError}
                />
              )}

              {step === "content" && (
                <ContentView result={result} clientId={clientId} onRestart={handleRestart} />
              )}
            </div>

            <CaptainPanel clientId={clientId} />
          </div>
        )}
      </main>
    </div>
  );
}
